import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { assignTicketSchema, type AssignTicketInput } from '@pms/shared';
import { techSupportService } from '@/services/tech-support.service';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface StaffUser { id: string; name: string; }

interface Props {
  ticketId: string;
  currentAssignee: { id: string; name: string } | null;
  onClose: () => void;
}

export function TicketAssignModal({ ticketId, currentAssignee, onClose }: Props) {
  const qc = useQueryClient();

  const { data: staffData } = useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => api.get<{ data: StaffUser[] }>('/users?limit=200&status=active'),
  });
  const staff: StaffUser[] = staffData?.data?.data ?? [];

  const { register, control, handleSubmit } = useForm<AssignTicketInput>({
    resolver: zodResolver(assignTicketSchema),
    defaultValues: { assigned_to: currentAssignee?.id ?? null, note: '' },
  });

  const mutation = useMutation({
    mutationFn: (data: AssignTicketInput) => techSupportService.assignTicket(ticketId, data),
    onSuccess: () => {
      toast.success('Ticket assigned');
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets', ticketId] });
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets'] });
      onClose();
    },
    onError: () => toast.error('Failed to assign ticket'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Ticket</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Assign To</Label>
            <Controller
              name="assigned_to"
              control={control}
              render={({ field }) => (
                <Select value={field.value ?? '_unassigned'} onValueChange={(v) => field.onChange(v === '_unassigned' ? null : v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unassigned">Unassigned</SelectItem>
                    {staff.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="note">Note (optional)</Label>
            <Textarea id="note" {...register('note')} rows={3} placeholder="Add a note about this assignment…" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
